import React, { useRef } from 'react'
import { TrackChangeType } from 'models/changes'
import { ELEMENT_TYPE } from '../../constants/elementTypes'
import { resizeInput } from './uomPillState'

export const MIN_INLINE_INPUT_WIDTH_CH = 4

export interface UOMPillContentProps {
    /** Ordered list of a pill's child elements (Text and UnitsOfMeasure entries) */
    elements: any[]
    /** CSS class applied to every text input */
    inputClassName: string
    /** Ref to the pill's root DOM element; forwarded to each cloned UOM child */
    pillRef: React.RefObject<HTMLElement>
    /** Map kept in sync with live input DOM refs for programmatic focus routing */
    textInputRefs: React.MutableRefObject<Map<string, HTMLInputElement>>
    /**
     * Called when a text input receives focus.
     * Receives a `getFocusedElState` callback so the caller can read the live
     * typed value and cursor position when the Insert UOM action fires.
     */
    onTextFocus: (
        elId: string,
        getFocusedElState: () => { value: string; cursorPos: number }
    ) => void
    /** Called on every text input keystroke with the element id and new value */
    onTextChange: (elId: string, value: string) => void
    /** Called when the user presses Escape on any text input — cancels the whole pill */
    onEscape?: () => void
    /** UOM child components (UnitsOfMeasure instances) injected by the parent */
    children: React.ReactNode
}

/**
 * Renders interleaved text inputs and UOM editor components for a pill that
 * contains UnitsOfMeasure children. Iterates `elements` in order so N UOMs
 * are handled correctly — each UnitsOfMeasure entry consumes the next child.
 */
export const UOMPillContent = React.memo(function UOMPillContent({
    elements,
    inputClassName,
    pillRef,
    textInputRefs,
    onTextFocus,
    onTextChange,
    onEscape,
    children,
}: UOMPillContentProps) {
    // Track last-known cursor positions so getFocusedElState can reconstruct the
    // split point when Insert UOM fires (blur happens before the button click).
    const cursorPosRef = useRef<Map<string, number>>(new Map())
    const childArray = React.Children.toArray(children)
    let uomCount = 0
    return (
        <>
            {elements.map((el: any) => {
                if (el.type === ELEMENT_TYPE.TEXT) {
                    return (
                        <input
                            key={el.id}
                            ref={(node) => {
                                if (node) {
                                    textInputRefs.current.set(el.id, node)
                                    resizeInput(node)
                                } else {
                                    textInputRefs.current.delete(el.id)
                                }
                            }}
                            type="text"
                            defaultValue={el.text ?? ''}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={() =>
                                onTextFocus(el.id, () => {
                                    const input = textInputRefs.current.get(
                                        el.id
                                    )
                                    const value = input?.value ?? el.text ?? ''
                                    const cursorPos =
                                        cursorPosRef.current.get(el.id) ??
                                        value.length
                                    return { value, cursorPos }
                                })
                            }
                            onBlur={(e) => {
                                cursorPosRef.current.set(
                                    el.id,
                                    e.target.selectionStart ?? 0
                                )
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    e.stopPropagation()
                                    onEscape?.()
                                }
                            }}
                            onChange={(e) => {
                                resizeInput(e.currentTarget)
                                onTextChange(el.id, e.currentTarget.value)
                            }}
                            className={inputClassName}
                            style={{
                                pointerEvents: 'auto',
                                cursor: 'text',
                                minWidth: `${MIN_INLINE_INPUT_WIDTH_CH}ch`,
                            }}
                        />
                    )
                }
                if (el.type === ELEMENT_TYPE.UNITS_OF_MEASURE) {
                    // Only ADDED UOMs are passed as children (getUOMPillChildren filters).
                    // Skip non-ADDED elements without consuming a childArray slot — otherwise
                    // a DELETED UOM would shift the index and the next ADDED UOM would render
                    // the wrong child component.
                    if (el.changeType !== TrackChangeType.ADDED) return null
                    const uomChild = childArray[uomCount++]
                    return uomChild
                        ? React.cloneElement(uomChild as React.ReactElement, {
                              pillRef,
                          })
                        : null
                }
                return null
            })}
        </>
    )
})
